import React, { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, LabelList,
} from 'recharts';

import PanelCard from '../../performance-operations/components/PanelCard';
import { Sub, Table } from '../../performance-operations/styles';
import { usePanel } from '../../performance-operations/config';
import {
  fetchComponents, formatNum, metaOf, useBandColors,
} from '../config';

// PM analitik P2 — E03 orta sağ: hedef ve yetkinlik bileşenleri için gruplanmış sütun (tümü + bant başına ortalama, 0–100) ve
// dağılım genişliği için kutu grafiği (min, Q1, medyan, Q3, maks). Bileşik puan (M52) yalnız iki bileşen ve geçerli ağırlıkla
// üretilir; eksik bileşen 0 sayılmaz. Gizli hücre (n < k ve tamamlayıcı) çubuk çizmez, tabloda "Gizli" yazar.
const SectionTitle = styled.div`
  margin: 12px 0 6px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const BOX_LEFT = 90;
const BOX_WIDTH = 200;
const BOX_ROW = 30;

function BoxPlot({ boxes, labelOf }) {
  const theme = useTheme();
  const colors = useBandColors();
  const { t, i18n } = useTranslation();
  const x = (v) => BOX_LEFT + (Math.max(0, Math.min(100, Number(v))) / 100) * BOX_WIDTH;
  const height = boxes.length * BOX_ROW + 18;
  const font = theme.metrics.font.size.xs;
  return (
    <svg
      width="100%" viewBox={`0 0 ${BOX_LEFT + BOX_WIDTH + 10} ${height}`} role="img" data-boxplot="1"
      aria-label={t('performance.results.analytics.components.boxAria')} style={{ display: 'block' }}
    >
      {[0, 25, 50, 75, 100].map((tick) => (
        <g key={tick}>
          <line x1={x(tick)} x2={x(tick)} y1={4} y2={height - 14} stroke={theme.colors.surface.border} />
          <text x={x(tick)} y={height - 2} textAnchor="middle" fontSize={font} fill={theme.colors.text.muted}>{tick}</text>
        </g>
      ))}
      {boxes.map((b, i) => {
        const cy = 8 + i * BOX_ROW + BOX_ROW / 2;
        const color = colors[b.component] || colors.goal;
        const ok = !b.suppressionReason && b.median !== null && b.median !== undefined;
        return (
          <g key={b.component} data-box={b.component}>
            <text x={BOX_LEFT - 6} y={cy + 4} textAnchor="end" fontSize={font} fill={theme.colors.text.primary}>{labelOf(b.component)}</text>
            {ok ? (
              <>
                <line x1={x(b.min)} x2={x(b.max)} y1={cy} y2={cy} stroke={color} strokeWidth={2} />
                <line x1={x(b.min)} x2={x(b.min)} y1={cy - 6} y2={cy + 6} stroke={color} strokeWidth={2} />
                <line x1={x(b.max)} x2={x(b.max)} y1={cy - 6} y2={cy + 6} stroke={color} strokeWidth={2} />
                <rect x={x(b.q1)} y={cy - 9} width={Math.max(2, x(b.q3) - x(b.q1))} height={18} rx={4} fill={theme.colors.surface.card} stroke={color} strokeWidth={2} />
                <line x1={x(b.median)} x2={x(b.median)} y1={cy - 9} y2={cy + 9} stroke={theme.colors.text.primary} strokeWidth={2} />
                <title>{t('performance.results.analytics.components.boxTitle', {
                  min: formatNum(b.min, i18n.language), q1: formatNum(b.q1, i18n.language), median: formatNum(b.median, i18n.language),
                  q3: formatNum(b.q3, i18n.language), max: formatNum(b.max, i18n.language), n: b.n,
                })}</title>
              </>
            ) : (
              <text x={BOX_LEFT + 4} y={cy + 4} fontSize={font} fill={theme.colors.text.muted}>
                {b.suppressionReason ? t('performance.results.analytics.suppressed') : t('performance.results.analytics.noData')}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

export default function ComponentsPanel({ filters, prelim = false, span = 6, onMeta }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const theme = useTheme();
  const colors = useBandColors();
  const [table, setTable] = useState(false);
  const key = JSON.stringify(filters);
  const panel = usePanel(() => fetchComponents(filters, { prelim }), [key, prelim], !!filters.periodId);
  useEffect(() => { if (panel.data) onMeta?.('components', metaOf(panel.data)); }, [panel.data, onMeta]);

  const data = panel.data;
  const components = data?.components || [];
  const groups = data?.groups || [];
  const all = groups.find((g) => g.key === 'all');
  const empty = !data || components.length === 0 || !all || all.stats.every((s) => !s.n && !s.suppressionReason);
  const label = (c) => t(`performance.results.analytics.components.${c}`);
  const groupLabel = (k) => (k === 'all' ? t('performance.results.analytics.components.all') : t(`performance.results.analytics.band.${k}`));
  const stat = (g, c) => g.stats.find((s) => s.component === c) || {};
  const rows = groups.map((g) => {
    const row = { key: g.key, name: groupLabel(g.key) };
    components.forEach((c) => {
      const s = stat(g, c);
      row[c] = s.mean === null || s.mean === undefined ? null : Number(s.mean);
      row[`${c}Label`] = s.mean === null || s.mean === undefined ? '' : formatNum(s.mean, lang);
    });
    return row;
  });
  const cellText = (s) => {
    if (s.suppressionReason) return t('performance.results.analytics.suppressed');
    if (s.mean === null || s.mean === undefined) return t('performance.results.analytics.noData');
    return `${formatNum(s.mean, lang)} (n = ${formatNum(s.n, lang, 0)})`;
  };

  return (
    <PanelCard
      panelKey="components" title={t('performance.results.analytics.components.title')} span={span}
      loading={panel.loading} error={panel.error} onRetry={panel.retry}
      empty={empty} emptyText={prelim ? t('performance.results.analytics.dist.noPreliminary') : t('performance.results.analytics.dist.noFinal')}
      tableToggle showTable={table} onToggleTable={() => setTable((v) => !v)}
    >
      {data?.combinedUnavailableReason && data.combinedUnavailableReason !== 'singleSystem' ? (
        <Sub data-note="combined">{t(`performance.results.analytics.components.combinedReason.${data.combinedUnavailableReason}`)}</Sub>
      ) : null}
      {data?.goalWeight !== null && data?.goalWeight !== undefined ? (
        <Sub data-note="weights">{t('performance.results.analytics.components.weights', { goal: data.goalWeight, feedback: data.feedbackWeight })}</Sub>
      ) : null}
      {table ? (
        <>
          <Table data-view="table">
            <thead>
              <tr>
                <th>{t('performance.results.analytics.components.group')}</th>
                {components.map((c) => <th key={c}>{label(c)}</th>)}
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <tr key={g.key} data-row={g.key}>
                  <td>{groupLabel(g.key)}</td>
                  {components.map((c) => <td key={c}>{cellText(stat(g, c))}</td>)}
                </tr>
              ))}
            </tbody>
          </Table>
          <SectionTitle>{t('performance.results.analytics.components.spread')}</SectionTitle>
          <Table data-view="box-table">
            <thead>
              <tr>
                <th>{t('performance.results.analytics.components.component')}</th>
                <th>{t('performance.results.analytics.components.min')}</th>
                <th>{t('performance.results.analytics.components.q1')}</th>
                <th>{t('performance.results.analytics.components.median')}</th>
                <th>{t('performance.results.analytics.components.q3')}</th>
                <th>{t('performance.results.analytics.components.max')}</th>
                <th>N</th>
              </tr>
            </thead>
            <tbody>
              {(data?.boxes || []).map((b) => (
                <tr key={b.component} data-row={`box-${b.component}`}>
                  <td>{label(b.component)}</td>
                  {b.suppressionReason ? (
                    <td colSpan={6}>{t('performance.results.analytics.suppressed')}</td>
                  ) : (
                    <>
                      <td>{formatNum(b.min, lang)}</td>
                      <td>{formatNum(b.q1, lang)}</td>
                      <td>{formatNum(b.median, lang)}</td>
                      <td>{formatNum(b.q3, lang)}</td>
                      <td>{formatNum(b.max, lang)}</td>
                      <td>{formatNum(b.n, lang, 0)}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      ) : (
        <>
          <div style={{ width: '100%', height: 220 }} data-view="chart">
            <ResponsiveContainer>
              <BarChart data={rows} margin={{ top: 20, right: 8, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: theme.metrics.font.size.xs }} interval={0} />
                <YAxis domain={[0, 100]} allowDecimals={false} tick={{ fontSize: theme.metrics.font.size.xs }} />
                <Tooltip formatter={(value) => formatNum(value, lang)} />
                <Legend wrapperStyle={{ fontSize: theme.metrics.font.size.xs }} />
                {components.map((c) => (
                  <Bar key={c} dataKey={c} name={label(c)} fill={colors[c]} radius={[4, 4, 0, 0]}>
                    <LabelList dataKey={`${c}Label`} position="top" style={{ fontSize: theme.metrics.font.size.xs, fill: theme.colors.text.primary }} />
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <SectionTitle>{t('performance.results.analytics.components.spread')}</SectionTitle>
          <BoxPlot boxes={data?.boxes || []} labelOf={label} />
        </>
      )}
    </PanelCard>
  );
}
